import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import { createConcreteira, deleteConcreteira, getConcreteira, updateConcreteira } from '../services/concreteiraService';
import { getSessionClientId } from '../services/authSession';
import { getAllCidades, getCidadesByUf, UF_OPTIONS } from '../services/cidadeService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import SearchAutocompleteSelect from '../components/SearchAutocompleteSelect';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import useSubmitLock from '../hooks/useSubmitLock';

const mockCidades = [
  { id: 1, nome: 'Sao Paulo' }, { id: 2, nome: 'Campinas' }, { id: 3, nome: 'Santos' },
];

const INITIAL_FORM_DATA = { idEmpresa: '', nomeConcreteira: '', idCidade: '' };

// Normaliza uma concreteira da API (resolve cidade/empresa aninhadas) para o formato da tela.
function normalizeConcreteira(item) {
  // AJUSTE BACKEND AQUI: chaves aceitas para resposta de concreteira.
  const cidade = item.cidade || item.cidadeDTO || item.cidadeResponse || null;
  const empresa = item.empresa || item.empresaDTO || item.empresaResponse || null;

  return {
    idConcreteira: item.id_concreteira || item.idConcreteira || item.id || Date.now(),
    idCliente: item.id_cliente || item.idCliente || empresa?.idCliente || '',
    razaoSocial: item.razao_social || item.razaoSocial || empresa?.razaoSocial || '',
    nomeConcreteira: item.nome_concreteira || item.nomeConcreteira || item.nome || '',
    idCidade: item.id_cidade || item.idCidade || cidade?.idCidade || cidade?.nome || '',
    nomeCidade: item.nome_cidade || item.nomeCidade || cidade?.nomeCidade || '',
    uf: item.uf || item.siglaUf || item.sigla_uf || cidade?.siglaUf || cidade?.sigla_uf || cidade?.uf || '',
    criadoEm: item.criado_em || item.criadoEm || new Date().toISOString(),
  };
}

// Formata uma data ISO para o padrão pt-BR (ou '-' se vazia/inválida).
function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR');
}

// Tela de Gestão de Concreteiras: CRUD com vínculo a empresa e cidade (UF + autocomplete).
function ConcreteirasPage() {
  const sessionClientId = getSessionClientId();
  const [selectedUf, setSelectedUf] = useState('MT');
  const [cidades, setCidades] = useState([]);
  const [cidadesTodas, setCidadesTodas] = useState(mockCidades);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConcreteiraId, setEditingConcreteiraId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [concreteiras, setConcreteiras] = useState([]);
  const [viewConcreteira, setViewConcreteira] = useState(null);

  // Carrega concreteiras ao montar.
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      // AJUSTE BACKEND AQUI: endpoints de concreteira para montar listagem.
      const [concreteirasResult] = await Promise.allSettled([
        getConcreteira(),
      ]);

      if (!isMounted) return;

      if (concreteirasResult.status === 'fulfilled' && Array.isArray(concreteirasResult.value)) {
        setConcreteiras(concreteirasResult.value.map(normalizeConcreteira));
      }

      setLoading(false);
    };

    loadData().catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Carrega todas as cidades para resolver o nome na listagem.
    const loadAllCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: carga global de cidades para resolver nome na listagem.
        const data = await getAllCidades();
        if (isMounted) {
          setCidadesTodas(Array.isArray(data) && data.length > 0 ? data : mockCidades);
        }
      } catch (error) {
        if (isMounted) {
          setCidadesTodas(mockCidades);
        }
      }
    };

    loadAllCidades();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Recarrega as cidades do formulário sempre que a UF selecionada muda.
    const loadCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de cidades por UF para o formulario.
        const data = await getCidadesByUf(selectedUf);
        if (isMounted) {
          setCidades(Array.isArray(data) && data.length > 0 ? data : mockCidades);
        }
      } catch (error) {
        if (isMounted) {
          setCidades(mockCidades);
        }
      }
    };

    loadCidades();

    return () => {
      isMounted = false;
    };
  }, [selectedUf]);

  // Índice id da cidade -> nome, para exibir/buscar pelo nome na listagem.
  const cidadeMap = useMemo(() => {
    return cidadesTodas.reduce((acc, item) => {
      const cidadeId = item.idCidade || item.id;
      const cidadeNome = item.nomeCidade || item.nome;
      acc[String(cidadeId)] = cidadeNome;
      return acc;
    }, {});
  }, [cidadesTodas]);

  const filteredConcreteiras = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return concreteiras;

    return concreteiras.filter((item) => {
      const nome = item.nomeConcreteira || '';
      const cidade = cidadeMap[String(item.idCidade)] || '';
      return (
        nome.toLowerCase().includes(normalized) ||
        cidade.toLowerCase().includes(normalized)
      );
    });
  }, [query, concreteiras, cidadeMap]);

  const stats = useMemo(() => {
    const total = concreteiras.length;
    const comCidade = concreteiras.filter((item) => item.idCidade).length;
    return { total, comCidade };
  }, [concreteiras]);

  // Handler de onChange por campo do formulário.
  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Abre o modal em modo de criação (reseta formulário e UF padrão MT).
  const openModal = () => {
    setFormErrors({});
    setEditingConcreteiraId(null);
    setSelectedUf('MT');
    setFormData(INITIAL_FORM_DATA);
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com os dados da concreteira.
  const openEditModal = (item) => {
    setFormErrors({});
    setEditingConcreteiraId(item.idConcreteira);
    setSelectedUf((item.uf || item.UF || 'MT').toUpperCase());
    setFormData({
      nomeConcreteira: item.nomeConcreteira || '',
      idCidade: item.idCidade ? String(item.idCidade) : '',
    });
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingConcreteiraId(null);
    setFormErrors({});
  };

  // Valida nome (obrigatório, máx. 50) e cidade (obrigatória); retorna mapa de erros.
  const validateForm = () => {
    const errors = {};
    if (!formData.nomeConcreteira.trim()) {
      errors.nomeConcreteira = 'Nome da concreteira obrigatório.';
    }
    if (formData.nomeConcreteira.trim().length > 50) {
      errors.nomeConcreteira = 'Nome da concreteira deve ter no máximo 50 caracteres.';
    }
    if (!formData.idCidade) {
      errors.idCidade = 'Cidade obrigatória.';
    }
    return errors;
  };

  // Submete o form: valida, cria/atualiza na API e completa nome de empresa/cidade na lista local.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingConcreteiraId) {
        // AJUSTE BACKEND AQUI: fluxo de update de concreteira.
        const { payload } = await updateConcreteira(editingConcreteiraId, formData, sessionClientId);

        // Busca o nomeCidade e uf da cidade selecionada para atualizar a grid imediatamente
        const cidadeSelecionada = cidades.find(c => String(c.idCidade || c.id) === String(payload.idCidade)) ||
          cidadesTodas.find(c => String(c.idCidade || c.id) === String(payload.idCidade));
        const nomeCidade = cidadeSelecionada ? (cidadeSelecionada.nomeCidade || cidadeSelecionada.nome) : '';
        const uf = cidadeSelecionada ? (cidadeSelecionada.siglaUf || cidadeSelecionada.uf || selectedUf) : selectedUf;

        setConcreteiras((prev) =>
          prev.map((item) => (item.idConcreteira === editingConcreteiraId ? { ...item, ...payload, nomeCidade, uf } : item))
        );
        showSuccessToast('Concreteira atualizada com sucesso.');
      } else {
        // AJUSTE BACKEND AQUI: fluxo de criacao de concreteira.
        const { response: created, payload } = await createConcreteira(formData, sessionClientId);

        const normalized = created
          ? normalizeConcreteira(created)
          : { idConcreteira: Date.now(), ...payload, criadoEm: new Date().toISOString() };

        if (!normalized.nomeCidade && normalized.idCidade) {
          const cidadeSelecionada = cidades.find(c => String(c.idCidade || c.id) === String(normalized.idCidade)) ||
            cidadesTodas.find(c => String(c.idCidade || c.id) === String(normalized.idCidade));
          if (cidadeSelecionada) normalized.nomeCidade = cidadeSelecionada.nomeCidade || cidadeSelecionada.nome;
        }

        setConcreteiras((prev) => [normalized, ...prev]);
        showSuccessToast('Concreteira cadastrada com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar a concreteira.' });
    }
  });

  // Exclui a concreteira após confirmação e remove da lista local.
  const handleDelete = async (idConcreteira) => {
    const confirm = window.confirm("Deseja realmente excluir esta concreteira?");
    if (!confirm) return;

    try {
      // AJUSTE BACKEND AQUI: exclusao de concreteira por id.
      await deleteConcreteira(idConcreteira);
      setConcreteiras((prev) => prev.filter((item) => item.idConcreteira !== idConcreteira));
      showSuccessToast('Concreteira excluída com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir a concreteira.');
    }
  };
  return (
    <div className="concreteiras-page space-y-6">
      <section className="concreteiras-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de concreteiras</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Cadastro de concreteiras.
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nova concreteira
        </button>
      </section>

      <section className="concreteiras-metrics grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        {/*<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com cidade</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comCidade}</p>
        </div>*/}
      </section>

      <section className="concreteiras-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por concreteira ou cidade"
            resultCount={filteredConcreteiras.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Concreteira</th>
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredConcreteiras.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`sk-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 4 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredConcreteiras.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhuma concreteira encontrada.
                  </td>
                </tr>
              ) : (
                filteredConcreteiras.map((item) => (
                  <tr key={item.idConcreteira} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{item.nomeConcreteira || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{item.nomeCidade || '-'}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewConcreteira(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.idConcreteira)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredConcreteiras.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhuma concreteira encontrada.
            </div>
          ) : (
            filteredConcreteiras.map((item) => (
              <div key={item.idConcreteira} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-slate-900">{item.nomeConcreteira || '-'}</p>
                <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600">
                  <span>Cidade: {item.nomeCidade || '-'}</span>
                  <span>Criado em: {formatDate(item.criadoEm)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewConcreteira(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.idConcreteira)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewConcreteira}
        onClose={() => setViewConcreteira(null)}
        title="Detalhes da concreteira"
        sections={viewConcreteira ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Concreteira', value: viewConcreteira.nomeConcreteira || '-' },
              { label: 'Cidade', value: viewConcreteira.nomeCidade || cidadeMap[String(viewConcreteira.idCidade)] || '-' },
              { label: 'UF', value: viewConcreteira.uf || '-' },
              { label: 'Criado em', value: formatDate(viewConcreteira.criadoEm) },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingConcreteiraId ? 'Editar concreteira' : 'Cadastrar concreteira'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-6 space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Nome da concreteira <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  maxLength={50}
                  value={formData.nomeConcreteira}
                  onChange={handleFieldChange('nomeConcreteira')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.nomeConcreteira ? (
                  <p className="text-xs text-rose-600">{formErrors.nomeConcreteira}</p>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">UF <span className="text-rose-500">*</span> </label>
                  <select
                    value={selectedUf}
                    onChange={(event) => {
                      const uf = event.target.value;
                      setSelectedUf(uf);
                      setFormData((prev) => ({ ...prev, idCidade: '' }));
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                  >
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Cidade <span className="text-rose-500">*</span></label>
                  <SearchAutocompleteSelect
                    value={formData.idCidade}
                    onChange={handleFieldChange('idCidade')}
                    placeholder="Buscar cidade"
                    options={cidades.map((cidade) => ({
                      id: cidade.idCidade || cidade.id,
                      label: cidade.nomeCidade || cidade.nome,
                    }))}
                  />
                  {formErrors.idCidade ? <p className="text-xs text-rose-600">{formErrors.idCidade}</p> : null}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? 'Salvando...' : editingConcreteiraId ? 'Salvar alterações' : 'Salvar concreteira'}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ConcreteirasPage;



